import React from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';

export const About = () => {
  const handleVideoClick = () => {
    window.open('https://youtu.be/TOWLFGV_4BY', '_blank');
  };

  return (
    <section
      id="about"
      className="py-20 md:py-32 px-4 md:px-6 lg:px-8 bg-[#F2F0E9]"
      data-testid="about-section"
    >
      <div className="container mx-auto max-w-7xl">
        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Who We Are - Spans 2 columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="md:col-span-2 bg-white border border-[#E2E0D6] p-8 rounded-2xl hover:shadow-lg transition-shadow duration-300"
            data-testid="who-we-are-card"
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1A2D23] mb-6">
              Who We Are
            </h2>
            <div className="space-y-6 max-w-[550px]">
              {/* Lead text */}
              <p className="text-lg leading-relaxed text-[#5C6B63]">
                We combine environmental education and documentary filmmaking to give visibility to real environmental action.
              </p>
              
              {/* Core points */}
              <div className="space-y-4">
                <div>
                  <p className="text-base font-semibold text-[#1A2D23]">Education</p>
                  <p className="text-base leading-relaxed text-[#5C6B63]">connects people directly with nature</p>
                </div>
                <div>
                  <p className="text-base font-semibold text-[#1A2D23]">Documentary storytelling</p>
                  <p className="text-base leading-relaxed text-[#5C6B63]">gives visibility to environmental work</p>
                </div>
              </div>
              
              {/* Closing paragraph */}
              <p className="text-base leading-relaxed text-[#5C6B63]">
                We collaborate with NGOs, institutions, and local communities across Europe to support conservation through learning, participation, and credible communication.
              </p>
            </div>
          </motion.div>

          {/* Video Thumbnail Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white border border-[#E2E0D6] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer group relative"
            data-testid="about-video-card"
            onClick={handleVideoClick}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/3ycm6oek_Screenshot%202026-04-06%20at%2018.30.46.png"
              alt="Azores: Meet the Giants"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            
            {/* Play button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Play className="w-6 h-6 text-[#1A2D23] ml-1" fill="#1A2D23" />
              </div>
            </div>
            
            {/* Text overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-xs uppercase tracking-widest text-white/90 mb-1 drop-shadow-md">Field Work</p>
              <p className="text-sm font-medium text-white drop-shadow-md">Azores: Meet the Giants</p>
            </div>
          </motion.div>

          {/* Our Mission - Spans full width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:col-span-3 bg-[#1A2D23] text-[#FDFCF8] p-8 rounded-2xl hover:shadow-lg transition-shadow duration-300"
            data-testid="mission-card"
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              Our Mission
            </h2>
            <p className="text-lg md:text-xl leading-relaxed">
              Our mission is to advance biodiversity conservation through
              environmental education and documentary storytelling — creating
              learning experiences and films that make environmental work visible,
              understandable, and lasting.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;